import { useEffect, useRef } from 'react';

/**
 * Document-wide pointer tracking for hover overlays that sit outside the editor (table "+" bars, resizer).
 * - `onMove` runs at most once per frame; `onScroll` fires on any scroll, since fixed overlays go stale.
 * - Mouse moves with a button held are skipped, so a drag in progress isn't disturbed.
 */
export const usePointerTracking = (handlers: { onMove: (event: MouseEvent) => void; onScroll: () => void }, enabled: boolean) => {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!enabled) return;

    let frame = 0;
    const onMove = (event: MouseEvent) => {
      if (event.buttons !== 0) return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => handlersRef.current.onMove(event));
    };
    const onScroll = () => handlersRef.current.onScroll();

    document.addEventListener('mousemove', onMove);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [enabled]);
};
